import { CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { LicenseInfo } from '@mui/x-license';
import { AthleteSubscriptionsProvider } from 'contexts/AthleteContext';
import { OrganizationsProvider } from 'contexts/OrganizationsContext';
import { useNavigationContext } from 'Layouts/Navigation/NavigationContext';
import LeavePagePrompt from 'Layouts/Partials/LeavePagePrompt';
import SetNavigation from 'Layouts/Partials/SetNavigation';
import routeconfig, { RouteConfigProps } from 'Layouts/routeconfig';
import React, { useEffect } from 'react';
import { CookiesProvider } from 'react-cookie';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router, useLocation, Redirect, Route, Switch } from 'react-router-dom';
import { authSelector } from 'redux/reducers/auth';
import { userSelector } from './redux/reducers/user';
import { DefaultTheme } from 'themes';
import { QueryParamProvider } from 'use-query-params';
import { ChatProvider } from './contexts/ChatContext';
import { PusherProvider } from './contexts/PusherContext';
import { UserProvider } from './contexts/UserContext';
import NoOrganizationsWarning from './pages/Organization/NoOrganizationsWarning';
import { MessageProvider, useMessage } from './contexts/MessageContext';
import Lottie from 'lottie-react';
import animationData from 'lottie-animations/checkbox.json';
import { AdminProvider } from './contexts/AdminContext';
import { ShoppingCartProvider } from './contexts/ShoppingCartContext';
import { SegmentAnalyticsProvider } from './contexts/SegmentAnalyticsContext';
import * as Sentry from '@sentry/react';

// Segment Analytics configuration
const segmentConfig = {
    writeKey: process.env.REACT_APP_SEGMENT_WRITE_KEY || 'YOUR_WRITE_KEY',
    // Optional additional configuration
    options: {
        integrations: {
            // Disable specific integrations if needed
            // 'Mixpanel': false,
        },
    },
};

LicenseInfo.setLicenseKey(
    'b21a033a559f84fbfa7b3eb84e429e74Tz0xMjA0NDMsRT0xNzkyMTA4Nzk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y',
);

// Component to handle the display of success messages from Stripe after a purchase.
const OverlayWithMessage: React.FC = () => {
    const location = useLocation();
    const { message, setMessage } = useMessage();
    const { isAuth } = useSelector(authSelector);
    const { userData } = useSelector(userSelector);

    // Stripe purchase snackbar message handler
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        if (!searchParams.get('stripeSuccess')) {
            return;
        }

        setMessage('Purchase was successful!');

        // Set a timeout to clear the message and query string param after 3 seconds
        const timer = setTimeout(() => {
            setMessage('');
            const currentUrl = window.location.href;
            const regex = /([?&])stripeSuccess=true(&|$)/;
            window.location.href = currentUrl
                .replace(regex, '&')
                .replace(/&$/, '')
                .replace(/\?$/, '');
        }, 3000);

        return () => clearTimeout(timer);
    }, [location, setMessage]);

    // Get user auth data for Sentry error reporting
    useEffect(() => {
        if (isAuth && userData) {
            Sentry.setUser({
                id: userData.uuid,
                username: userData.user_name,
                email: userData.email,
                name: `${userData.first_name} ${userData.last_name}`,
            });
        } else {
            Sentry.setUser(null);
        }
    }, [isAuth, userData]);

    if (!message) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
        >
            <Lottie animationData={animationData} style={{ width: '300px', height: '300px' }} />
            <p
                style={{
                    color: 'white',
                    marginTop: '20px',
                    fontSize: '24px',
                    textShadow: '2px 2px 4px #000000',
                }}
            >
                {message}
            </p>
        </div>
    );
};

const App = () => {
    const { isAuth } = useSelector(authSelector);
    const { isRedirectionBlocked, blockRedirection } = useNavigationContext();

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={DefaultTheme}>
                {/* Wrap the entire app with SegmentAnalyticsProvider */}
                <SegmentAnalyticsProvider
                    config={segmentConfig}
                    debug={process.env.NODE_ENV === 'development'}
                >
                    <AdminProvider>
                        <ShoppingCartProvider>
                            <LocalizationProvider
                                dateAdapter={AdapterLuxon}
                                localeText={{ start: 'After', end: 'Before' }}
                            >
                                <PusherProvider>
                                    <CssBaseline />

                                    <CookiesProvider>
                                        <Router>
                                            <QueryParamProvider ReactRouterRoute={Route}>
                                                <MessageProvider>
                                                    <OverlayWithMessage />
                                                </MessageProvider>
                                                <OrganizationsProvider>
                                                    <UserProvider>
                                                        <AthleteSubscriptionsProvider>
                                                            <ChatProvider>
                                                                <Switch>
                                                                    <Route
                                                                        exact={true}
                                                                        path={'/no-organizations'}
                                                                    >
                                                                        <NoOrganizationsWarning />
                                                                    </Route>
                                                                    {routeconfig.map(
                                                                        (
                                                                            config: RouteConfigProps,
                                                                            index,
                                                                        ) => {
                                                                            return (
                                                                                <Route
                                                                                    key={`route-${index}`}
                                                                                    exact={
                                                                                        config.exact
                                                                                    }
                                                                                    path={
                                                                                        config.path
                                                                                    }
                                                                                    render={(
                                                                                        history,
                                                                                    ) => {
                                                                                        // Unblock the router prompt when redirection happens
                                                                                        history.history.listen(
                                                                                            () =>
                                                                                                blockRedirection(
                                                                                                    false,
                                                                                                ),
                                                                                        );

                                                                                        if (
                                                                                            config.private &&
                                                                                            !isAuth
                                                                                        ) {
                                                                                            return (
                                                                                                <Redirect
                                                                                                    to={{
                                                                                                        pathname:
                                                                                                            '/login',
                                                                                                        state: {
                                                                                                            referrer:
                                                                                                                history
                                                                                                                    .location
                                                                                                                    .pathname +
                                                                                                                history
                                                                                                                    .location
                                                                                                                    .search,
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            );
                                                                                        }

                                                                                        if (
                                                                                            config.is_auth_route &&
                                                                                            isAuth
                                                                                        ) {
                                                                                            return (
                                                                                                <Redirect to="/home" />
                                                                                            );
                                                                                        }
                                                                                        const element =
                                                                                            React.createElement(
                                                                                                config.layout,
                                                                                                {},
                                                                                                React.createElement(
                                                                                                    config.component,
                                                                                                    {},
                                                                                                ),
                                                                                            );

                                                                                        const page =
                                                                                            element;

                                                                                        return (
                                                                                            <>
                                                                                                <SetNavigation
                                                                                                    config={
                                                                                                        config
                                                                                                    }
                                                                                                />
                                                                                                <LeavePagePrompt
                                                                                                    when={
                                                                                                        isRedirectionBlocked
                                                                                                    }
                                                                                                />
                                                                                                <>
                                                                                                    {
                                                                                                        page
                                                                                                    }
                                                                                                </>
                                                                                            </>
                                                                                        );
                                                                                    }}
                                                                                ></Route>
                                                                            );
                                                                        },
                                                                    )}
                                                                    <Route path="*">
                                                                        <Redirect to={'/home'} />
                                                                    </Route>
                                                                </Switch>
                                                            </ChatProvider>
                                                        </AthleteSubscriptionsProvider>
                                                    </UserProvider>
                                                </OrganizationsProvider>
                                            </QueryParamProvider>
                                        </Router>
                                    </CookiesProvider>
                                </PusherProvider>
                            </LocalizationProvider>
                        </ShoppingCartProvider>
                    </AdminProvider>
                </SegmentAnalyticsProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default App;
